import EmojiOne from 'emojione';
import escapeHtml from 'escape-html';
import isHtml from 'is-html';
import juiceClient from 'juice/client';
import { config } from 'lib-shared-backend-config';
import {
  Conversation,
  Message,
  MessageEmailRecipient,
  MetaConversation,
} from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';

import {
  convertPlainToHtml,
  getDirectionFormattedHtml,
} from 'app/utils/htmlFormatting';
import {
  addTargetAndRelToLinks,
  getMessageSanitizationChannel,
  SanitizeHTML,
} from 'app/utils/htmlSanitizer';
import markdownToHTML, { MarkdownProcessor } from 'app/utils/markdownToHTML';

import { EmailMessageType } from '../../components/conversation/reducers/emailMessage';

import {
  AttachmentFile,
  prepareAttachments,
  prepareInlineImages,
} from './attachments';
import { juiceOptions } from './juiceOptions';

const LINEBREAK = `<br /><br />`;

/**
 * Extracts the main message content from HTML email, removing quoted/nested
 * messages
 */
function extractMessageContent(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove hidden metadata elements (previous-message-reference, tracking pixels, etc.)
  const hiddenElements = doc.querySelectorAll(
    '[style*="display:none"], [style*="display: none"], [style*="width: 0px"], [style*="height: 0px"]',
  );
  hiddenElements.forEach((element) => element.remove());

  // Find the main message container (dixa_reply_container, dixa_compose_container, or dixa_auto_reply_container)
  const replyContainer = doc.querySelector(
    '.dixa_reply_container, .dixa_compose_container, .dixa_auto_reply_container',
  );

  if (replyContainer) {
    // Get the reply body content
    const replyBody = replyContainer.querySelector(
      '.dixa_reply_body, .dixa_compose_body, .dixa_auto_reply_body',
    );
    if (replyBody) {
      // Remove any remaining hidden elements within the body
      const hiddenInBody = replyBody.querySelectorAll(
        '[style*="display:none"], [style*="display: none"], [style*="width: 0px"], [style*="height: 0px"]',
      );
      hiddenInBody.forEach((element) => element.remove());

      return replyBody.innerHTML.trim();
    }
  }

  // If no container found, try to remove blockquotes and return remaining content
  const blockquotes = doc.querySelectorAll(
    'blockquote, .dixa_quote, .dixa_quoted_message',
  );
  blockquotes.forEach((blockquote) => blockquote.remove());

  return doc.body.innerHTML.trim();
}

/**
 * Formats email metadata header for transcript
 *
 * @param message The email message object.
 * @param conversation The conversation object, expected to have an `attributes`
 *   property with a `subject` field (e.g., { attributes: { subject: string }
 *   }).
 */
function getEmailMetadataHeader(
  message: Message,
  conversation?: Conversation,
): string {
  const direction = message.direction === 'inbound' ? 'Inbound' : 'Outbound';
  const date = new Date(message.created_at as string);

  // Format date with full timezone info
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  };
  const formattedDate = date.toLocaleString(navigator.language, dateOptions);

  let header = `<br /><strong>---------- ${direction} Email ----------</strong><br />`;

  // Add subject if available
  const subject = conversation?.attributes?.subject;
  if (subject) {
    header += `<strong>Subject:</strong> ${escapeHtml(subject)}<br />`;
  }

  if (message.from) {
    header += `<strong>From:</strong> ${escapeHtml(message.from)}<br />`;
  }

  if (message.to_list_v2 && message.to_list_v2.length > 0) {
    const toEmails = message.to_list_v2
      .map((recipient: MessageEmailRecipient) =>
        escapeHtml((recipient.email || recipient) as string),
      )
      .join(', ');
    header += `<strong>To:</strong> ${toEmails}<br />`;
  } else if (message.to) {
    header += `<strong>To:</strong> ${escapeHtml(message.to)}<br />`;
  }

  if (message.cc_list_v2 && message.cc_list_v2.length > 0) {
    const ccEmails = message.cc_list_v2
      .map((recipient: MessageEmailRecipient) =>
        escapeHtml((recipient.email || recipient) as string),
      )
      .join(', ');
    header += `<strong>CC:</strong> ${ccEmails}<br />`;
  }

  header += `<strong>Date:</strong> ${formattedDate}<br />`;
  header += `<br />`;

  return header;
}

/** Checks if a message should be skipped in the transcript */
function shouldSkipMessage(message: Message): boolean {
  const text = message.text?.trim();

  // Skip messages with no text or "(No text)"
  if (!text || text === '(No text)') {
    return true;
  }

  return false;
}

function getTranscriptMessage(
  message: Message,
  messageContent: string | null | undefined,
  isHtml: boolean,
  conversation?: Conversation,
): string {
  const date = new Date(message.created_at as string);

  // Format date with timezone
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  };
  const dateString = date.toLocaleString(navigator.language, dateOptions);

  const italicStart = isHtml ? '<i>' : '';
  const italicEnd = isHtml ? '</i>' : '';

  // Get author name or phone number for voice messages
  let authorInfo = message.author?.id
    ? message.author?.name || message.author?.email || ''
    : '';

  // For voice messages, always try to show phone number
  const telephonyChannels = ['voicemail', 'pstnphone', 'callback'];
  const isVoiceMessage =
    (message.channel && telephonyChannels.includes(message.channel)) ||
    message.voicemail_url;

  if (isVoiceMessage) {
    // Try message fields first, then conversation attributes
    const phoneNumber =
      message.a_phone_number ||
      message.b_phone_number ||
      message.from ||
      message.to ||
      conversation?.attributes?.calling_number ||
      conversation?.attributes?.a_phone_number ||
      conversation?.attributes?.b_phone_number ||
      conversation?.attributes?.callingnumber;

    if (phoneNumber) {
      // If there's a name, show "Name (phoneNumber)", otherwise just phoneNumber
      if (authorInfo && !phoneNumber.includes(authorInfo)) {
        authorInfo = `${authorInfo} (${phoneNumber})`;
      } else if (!authorInfo) {
        authorInfo = phoneNumber;
      }
    }
  }

  // Only add comma if there's author info
  const datePart = authorInfo
    ? `, ${italicStart}${dateString}${italicEnd}:`
    : `${italicStart}${dateString}${italicEnd}:`;

  return `${authorInfo}${datePart}${LINEBREAK}${messageContent}${LINEBREAK}`;
}

type MessagesTranscript = {
  transcript: string;
  authors: User[];
  attachments: AttachmentFile[];
};

// Malformed CSS in a single forwarded email must not take down the whole
// transcript — fall back to the uninlined html, as the email sanitizer does.
function safeJuice(html: string): string {
  try {
    return juiceClient(html, juiceOptions);
  } catch {
    return html;
  }
}

export function getMessagesTranscript(
  metaConversation: MetaConversation | null | undefined,
  rewrittenHtmlEmailMessages: EmailMessageType[],
): MessagesTranscript {
  const messagesToBeCopied: Message[] =
    metaConversation?.messages
      .filter((message: Message) =>
        ['conversation:add_message', 'conversation:message_added'].includes(
          message.type,
        ),
      )
      .sort((a: Message, b: Message) => {
        if (a.created_at == null) return 1;
        if (b.created_at == null) return -1;
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }) ?? [];

  const isEmailConversation = ['contactform', 'email'].includes(
    metaConversation?.conversation.initial_channel ?? '',
  );

  // For email conversations with HTML support, use the new clean approach
  if (isEmailConversation) {
    return messagesToBeCopied.reduce<MessagesTranscript>(
      (acc, message) => {
        // Always collect attachments first, even from messages without text
        const attachments: AttachmentFile[] = [
          ...acc.attachments,
          ...prepareAttachments(message.attached_files),
          ...prepareInlineImages(message.inline_images),
        ];

        // Skip messages with no text or "(No text)" for transcript content only
        if (shouldSkipMessage(message)) {
          return { ...acc, attachments };
        }

        // Add author to contact list
        const authors = acc.authors.some((a) => a?.id === message.author?.id)
          ? acc.authors
          : [...acc.authors, message.author];

        // Find the rewritten HTML for this message
        const messageId = message.email_original_content_url?.split('/').pop();
        const foundHtmlMessage = messageId
          ? rewrittenHtmlEmailMessages.find(
              (htmlMsg: EmailMessageType) => htmlMsg.messageId === messageId,
            )
          : null;

        // Add email metadata header
        const header = getEmailMetadataHeader(
          message,
          metaConversation?.conversation,
        );

        let messageTranscript: string;
        if (foundHtmlMessage && foundHtmlMessage.rewritten_html) {
          const juicedHtml = safeJuice(foundHtmlMessage.rewritten_html);
          // For inbound emails, use full content; for outbound, extract clean content
          const cleanContent =
            message.direction === 'inbound'
              ? juicedHtml
              : extractMessageContent(juicedHtml);

          messageTranscript = `${header}${cleanContent}${LINEBREAK}`;
        } else {
          // Fallback to markdown for messages without HTML
          const content = getMessageMarkup(message);
          messageTranscript = `${header}${content}${LINEBREAK}`;
        }

        // Prepend to keep newest messages at the top
        const transcript = `${messageTranscript}${acc.transcript}`;

        return {
          transcript,
          attachments,
          authors,
        };
      },
      {
        transcript: '',
        attachments: [],
        authors: [],
      },
    );
  }

  // For non-email channels or without HTML support, use markdown approach
  return messagesToBeCopied.reduce<MessagesTranscript>(
    (acc, message) => {
      // Always collect attachments first, even from messages without text
      const attachments: AttachmentFile[] = [
        ...acc.attachments,
        ...prepareAttachments(message.attached_files),
        ...prepareInlineImages(message.inline_images),
      ];

      // Skip messages with no text or "(No text)" for transcript content only
      if (shouldSkipMessage(message)) {
        return { ...acc, attachments };
      }

      // Add author to contact list
      const authors = acc.authors.some((a) => a?.id === message.author?.id)
        ? acc.authors
        : [...acc.authors, message.author];

      const messageContent = getMessageMarkup(message);
      const transcriptMessage = getTranscriptMessage(
        message,
        messageContent,
        false,
        metaConversation?.conversation,
      );

      // Prepend to keep newest messages at the top
      const transcript = `${transcriptMessage}${acc.transcript}`;

      return {
        transcript,
        attachments,
        authors,
      };
    },
    {
      transcript: '',
      attachments: [],
      authors: [],
    },
  );
}

export function prepareOriginalEmailUrl(url: string): string {
  // Adds https://files.dixa.io (or staging) as prefix if missing.
  if (/^https?:\/\//i.test(url) === false) {
    return config.url.userContent.replace(/\/$/, '') + url;
  }
  return url.replace(`${config.url.files}/`, config.url.userContent);
}

/** Returns proper markup based on the message context. */
export function getMessageMarkup(message: Message): string {
  const sanitizationChannel = getMessageSanitizationChannel(message);
  let preparedMarkup = SanitizeHTML.byChannel(
    message.text ?? '',
    sanitizationChannel,
  );

  if (message && (message.channel || message.type)) {
    if (message.channel === 'contactform') {
      if (message.direction === 'inbound' && !isHtml(preparedMarkup)) {
        preparedMarkup = getDirectionFormattedHtml(
          convertPlainToHtml(EmojiOne.shortnameToUnicode(preparedMarkup)),
        );
      } else {
        preparedMarkup = markdownToHTML(
          EmojiOne.shortnameToUnicode(preparedMarkup),
        );
      }
    } else if (message.channel === 'messenger') {
      const emojified = EmojiOne.shortnameToUnicode(preparedMarkup);
      preparedMarkup =
        message.content_type === 'Html' ? emojified : markdownToHTML(emojified);
    } else if (
      message.channel &&
      [
        'email',
        'widgetchat',
        'sms',
        'adachat',
        'digitalgeniuschat',
        'kindlychat',
        'genericapimessaging',
        'trustpilotreviews',
        'yotporeviews',
      ].includes(message.channel)
    ) {
      preparedMarkup = markdownToHTML(
        EmojiOne.shortnameToUnicode(preparedMarkup),
      );
    } else if (
      message.channel &&
      ['facebookmessenger', 'twitter'].includes(message.channel)
    ) {
      preparedMarkup = markdownToHTML(preparedMarkup);
    } else if (message.channel === 'whatsapp') {
      const whatsappMarkdownPreProcessors: MarkdownProcessor[] = [
        // Unescape common markdown characters: \* \- \_ \~ \> \` \[ \]
        (md) => md.replace(/\\([*_~`[\]>-])/g, '$1'),
        // Convert WhatsApp single-tilde strikethrough to markdown standard double-tilde
        // Match ~text~ and convert it to ~~text~~
        (md) => md.replace(/(?<!~)~(?!~)([^~\n]+?)~(?!~)/g, '~~$1~~'),
      ];

      // The order of execution is important here to ensure the correct markdown processing.
      const htmlFromMarkdown = markdownToHTML(
        message.text ?? '',
        whatsappMarkdownPreProcessors,
      );

      preparedMarkup = SanitizeHTML.byChannel(
        htmlFromMarkdown,
        sanitizationChannel,
      );
    } else if (
      ['conversation:wrapup_message', 'conversation:wrapup_added'].includes(
        message.type,
      )
    ) {
      preparedMarkup = markdownToHTML(
        EmojiOne.shortnameToUnicode(preparedMarkup),
      );
    }
  }

  const html = addTargetAndRelToLinks(preparedMarkup);
  return typeof html === 'string' ? html : '';
}
